import { ReloadOutlined } from '@ant-design/icons';
import { Button, Spin, Table, Tag, Typography } from 'antd';
import React, { useContext, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';

import StickyPageHeader from '../components/common/StickyPageHeader';
import MergeRatesModal from '../components/modals/MergeRatesModal';
import { AuthContext } from '../contexts/authContext';
import { useRateAuditByCarrier, useSelectedCarrier } from '../hooks/apiHooks';
import Layout from '../layout';
import { UserRole } from '../models/User';

const { Text } = Typography;

const DuplicateGroupTable = ({ carrierId, group, groupIndex }) => {
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const { isFetching, isLoading, refetch } = useRateAuditByCarrier(carrierId);

  const handleMergeSuccess = () => {
    refetch();
  };

  const classIds = new Set();
  group.rates.forEach((rate) => {
    Object.keys(rate.classRate || {}).forEach((classId) => {
      classIds.add(classId);
    });
  });

  // finding class name from any rate that has it
  const classColumns = Array.from(classIds).map((classId) => {
    const classIdNum = parseInt(classId);
    let className = classId;
    for (const rate of group.rates) {
      const name = rate.classRate?.[classIdNum]?.name;
      if (name) {
        className = name;
        break;
      }
    }
    return {
      ellipsis: true,
      key: `class_${classId}`,
      render: (_, rate) => {
        const value = rate.classRate?.[classIdNum]?.value;
        return value != null ? `$${value.toFixed(2)}` : '-';
      },
      title: className,
    };
  });

  const rateColumns = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Rate ID',
      width: 100,
    },
    ...classColumns,
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '-'),
      title: 'Updated At',
      width: 100,
    },
  ];

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: '4px',
        marginBottom: '24px',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          borderBottom: '1px solid #d9d9d9',
          padding: '12px 0px',
        }}
      >
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginLeft: '9px',
          }}
        >
          <Text strong>{group.origin}</Text>
          {group.rates[0]?.originShipTypeName && (
            <Tag color="blue">{group.rates[0].originShipTypeName}</Tag>
          )}
          <Text
            style={{ fontSize: '16px', marginRight: '8px' }}
            type="secondary"
          >
            →
          </Text>
          <Text strong>{group.destination}</Text>
          {group.rates[0]?.destinationShipTypeName && (
            <Tag color="blue">{group.rates[0].destinationShipTypeName}</Tag>
          )}
          {group.rates[0]?.modeName && (
            <Tag color="green">{group.rates[0].modeName}</Tag>
          )}
          <Button
            disabled={isLoading || isFetching}
            size="small"
            style={{ marginLeft: 'auto' }}
            type="primary"
            onClick={() => setIsMergeModalOpen(true)}
          >
            Merge
          </Button>
          <Tag
            color={group.duplicateCount > 2 ? 'red' : 'orange'}
            style={{ marginLeft: '8px' }}
          >
            {group.duplicateCount} duplicates
          </Tag>
        </div>
      </div>
      <Table
        bordered={true}
        className="audit-rates-table"
        columns={rateColumns}
        dataSource={group.rates}
        loading={isLoading || isFetching}
        pagination={false}
        rowClassName="rate-row-highlight"
        rowKey={(rate) => rate.id}
        scroll={{ x: 'max-content' }}
        size="small"
      />
      {isMergeModalOpen && (
        <MergeRatesModal
          carrierId={carrierId}
          group={group}
          onClose={() => setIsMergeModalOpen(false)}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
};

const CarrierAuditRatesPage = ({ disableLayout, routes }) => {
  const { authUser } = useContext(AuthContext);
  const { carrierId, selectedCarrier, status } = useSelectedCarrier();
  const { auditDetail, error, isFetching, isLoading, refetch } = useRateAuditByCarrier(
    carrierId
  );

  const isAdmin = useMemo(
    () => [UserRole.SUPER_ADMIN, UserRole.ADMIN].includes(authUser.role),
    [authUser.role]
  );

  const handleRefresh = () => {
    refetch();
  };

  if (status === 'error') {
    return <Redirect to="/" />;
  }

  const content = (
    <>
      <StickyPageHeader
        actions={
          <Button
            ghost
            icon={<ReloadOutlined />}
            loading={isLoading || isFetching}
            type="primary"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        }
        subtitle={selectedCarrier?.name}
        title="Audit Rates"
      />

      {isLoading || (isFetching && !auditDetail) ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <div style={{ color: 'red', padding: '20px' }}>
          Error loading audit data: {error.message}
        </div>
      ) : !auditDetail || auditDetail.duplicateGroups.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Text type="secondary">
            No duplicate rates found for this carrier.
          </Text>
        </div>
      ) : (
        <div>
          <Text style={{ display: 'block', marginBottom: 16 }} type="secondary">
            Please review the duplicate rates below and merge them into a single rate.
          </Text>
          {auditDetail.duplicateGroups.map((group, groupIndex) => (
            <DuplicateGroupTable
              key={`group-${groupIndex}`}
              carrierId={carrierId}
              group={group}
              groupIndex={groupIndex}
            />
          ))}
        </div>
      )}
    </>
  );

  if (disableLayout) {
    return content;
  }

  return (
    <Layout
      disableLoading
      menuIndex={isAdmin ? 1 : 0}
      routes={routes}
      showCarriersNavigation={isAdmin}
    >
      {content}
    </Layout>
  );
};

export default CarrierAuditRatesPage;
